import React, { forwardRef, useEffect, useRef, useState, useCallback } from 'react';
import { AlertCircle, ZoomIn, ZoomOut, RotateCcw, Move, Maximize2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BadgeCanvas = forwardRef(({ badge, image, showGuides, orientation, onImageTransformChange }, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageRotation, setImageRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Notify parent of transform changes
  useEffect(() => {
    if (onImageTransformChange) {
      onImageTransformChange({
        zoom: imageZoom,
        position: imagePosition,
        rotation: imageRotation
      });
    }
  }, [imageZoom, imagePosition, imageRotation, onImageTransformChange]);

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
    setImageRotation(0);
  };

  const handleFitToScreen = () => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleAutoCrop = () => {
    // Reset to fit within boundaries
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
    setImageRotation(0);
  };

  const handleMouseDown = (e) => {
    if (!image) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    if (!image) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setImageZoom(prev => Math.min(Math.max(prev + delta, 0.5), 3));
  }, [image]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        canvas.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel]);

  useEffect(() => {
    if (!badge || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    let designWidth, designHeight, badgeWidth, badgeHeight;

    if (badge.type === 'round') {
      designWidth = designHeight = badge.designArea;
      badgeWidth = badgeHeight = badge.badgeSize;
    } else if (badge.type === 'square') {
      designWidth = designHeight = badge.designArea;
      badgeWidth = badgeHeight = badge.badgeSize;
    } else {
      if (orientation === 'landscape') {
        designWidth = badge.designWidth;
        designHeight = badge.designHeight;
        badgeWidth = badge.badgeWidth;
        badgeHeight = badge.badgeHeight;
      } else {
        designWidth = badge.designHeight;
        designHeight = badge.designWidth;
        badgeWidth = badge.badgeHeight;
        badgeHeight = badge.badgeWidth;
      }
    }

    const scale = 4;
    canvas.width = designWidth * scale;
    canvas.height = designHeight * scale;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);

    // Draw background
    ctx.fillStyle = '#ffffff';
    if (badge.type === 'round') {
      ctx.beginPath();
      ctx.arc(designWidth / 2, designHeight / 2, designWidth / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, designWidth, designHeight);
    }

    if (image) {
      const img = new Image();
      img.onload = () => {
        ctx.save();

        // Create clipping path
        if (badge.type === 'round') {
          ctx.beginPath();
          ctx.arc(designWidth / 2, designHeight / 2, designWidth / 2, 0, Math.PI * 2);
          ctx.clip();
        }

        const imgRatio = img.width / img.height;
        const canvasRatio = designWidth / designHeight;

        let baseWidth, baseHeight, baseOffsetX, baseOffsetY;

        if (imgRatio > canvasRatio) {
          baseHeight = designHeight;
          baseWidth = designHeight * imgRatio;
          baseOffsetX = (designWidth - baseWidth) / 2;
          baseOffsetY = 0;
        } else {
          baseWidth = designWidth;
          baseHeight = designWidth / imgRatio;
          baseOffsetX = 0;
          baseOffsetY = (designHeight - baseHeight) / 2;
        }

        const zoomedWidth = baseWidth * imageZoom;
        const zoomedHeight = baseHeight * imageZoom;

        const canvasElement = canvas;
        const rect = canvasElement.getBoundingClientRect();
        const scaleFactorX = designWidth / rect.width;
        const scaleFactorY = designHeight / rect.height;

        const posX = imagePosition.x * scaleFactorX;
        const posY = imagePosition.y * scaleFactorY;

        const drawX = baseOffsetX + (baseWidth - zoomedWidth) / 2 + posX;
        const drawY = baseOffsetY + (baseHeight - zoomedHeight) / 2 + posY;

        // Apply rotation
        ctx.translate(designWidth / 2, designHeight / 2);
        ctx.rotate((imageRotation * Math.PI) / 180);
        ctx.translate(-designWidth / 2, -designHeight / 2);

        ctx.drawImage(img, drawX, drawY, zoomedWidth, zoomedHeight);
        ctx.restore();

        // Check overflow
        const bleedX = (designWidth - badgeWidth) / 2;
        const bleedY = (designHeight - badgeHeight) / 2;
        const overflow = drawX < bleedX || drawY < bleedY ||
          (drawX + zoomedWidth) > (designWidth - bleedX) ||
          (drawY + zoomedHeight) > (designHeight - bleedY);
        setIsOverflowing(overflow);

        // Draw guides and overflow indicator
        if (showGuides) {
          drawGuides(ctx, badge, designWidth, designHeight, badgeWidth, badgeHeight, overflow);
        }
      };
      img.src = image.url;
    } else {
      ctx.fillStyle = '#f0f0f0';
      if (badge.type === 'round') {
        ctx.beginPath();
        ctx.arc(designWidth / 2, designHeight / 2, designWidth / 2 - 10, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(10, 10, designWidth - 20, designHeight - 20);
      }

      ctx.fillStyle = '#999';
      ctx.font = '16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Upload an image', designWidth / 2, designHeight / 2);

      if (showGuides) {
        drawGuides(ctx, badge, designWidth, designHeight, badgeWidth, badgeHeight, false);
      }
    }
  }, [badge, image, showGuides, orientation, imageZoom, imagePosition, imageRotation]);

  const drawGuides = (ctx, badge, designWidth, designHeight, badgeWidth, badgeHeight, overflow) => {
    // Inner Cut Line (Solid Black)
    ctx.strokeStyle = overflow ? '#ef4444' : '#000000';
    ctx.lineWidth = 2; // Make it slightly thicker for visibility
    ctx.setLineDash([]); // Solid line as requested

    if (badge.type === 'round') {
      const bleed = (designWidth - badgeWidth) / 2;
      ctx.beginPath();
      ctx.arc(designWidth / 2, designHeight / 2, designWidth / 2 - bleed, 0, Math.PI * 2);
      ctx.stroke();

      // Outer Bleed Line (Light Grey/Guide)
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(designWidth / 2, designHeight / 2, designWidth / 2, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      const bleedX = (designWidth - badgeWidth) / 2;
      const bleedY = (designHeight - badgeHeight) / 2;

      // Inner Cut Line
      ctx.strokeRect(bleedX, bleedY, badgeWidth, badgeHeight);

      // Outer Bleed Line
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, designWidth, designHeight);
    }
  };

  if (!badge) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center">
        <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Badge Selected</h3>
        <p className="text-muted-foreground">Please select a badge size to get started</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center justify-center h-full space-y-6">
      <div className="space-y-2 text-center">
        <h3 className="text-xl font-semibold">{badge.label} Design Preview</h3>
        <p className="text-sm text-muted-foreground">{badge.description}</p>
      </div>

      {/* Overflow Warning */}
      {image && isOverflowing && (
        <div className="flex items-center gap-2 px-4 py-2 bg-destructive/10 border border-destructive/50 rounded-xl text-destructive">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">Image extends beyond cutting lines</span>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleAutoCrop}
            className="ml-2 h-7 rounded-lg"
          >
            Auto-Fit
          </Button>
        </div>
      )}

      {/* Zoom Controls */}
      {image && (
        <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-2xl border border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomOut}
            disabled={imageZoom <= 0.5}
            className="rounded-xl h-9 w-9 p-0"
            title="Zoom Out Image (Mouse Wheel)"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>

          <div className="px-4 py-1 bg-background rounded-lg border border-border min-w-[80px] text-center">
            <span className="text-sm font-medium">{Math.round(imageZoom * 100)}%</span>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleZoomIn}
            disabled={imageZoom >= 3}
            className="rounded-xl h-9 w-9 p-0"
            title="Zoom In Image (Mouse Wheel)"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <Button
            variant="ghost"
            size="sm"
            onClick={handleFitToScreen}
            className="rounded-xl h-9 w-9 p-0"
            title="Fit to Screen"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetZoom}
            className="rounded-xl h-9 w-9 p-0"
            title="Reset All"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>

          <div className="w-px h-6 bg-border mx-1" />

          <div className="flex items-center gap-2 px-2 text-xs text-muted-foreground">
            <Move className="w-3 h-3" />
            <span>Drag • Scroll to zoom</span>
          </div>
        </div>
      )}

      <div className="relative inline-block rounded-2xl border-2 border-border bg-muted/10 p-4">
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          className={`shadow-soft max-w-full h-auto ${image ? 'cursor-move' : 'cursor-default'} ${isOverflowing ? 'ring-2 ring-destructive' : ''}`}
          style={{
            maxHeight: '500px',
            userSelect: 'none'
          }}
        />
        {showGuides && (
          <div className="absolute -bottom-8 left-0 right-0 text-center">
            <span className={`inline-block px-3 py-1 ${isOverflowing ? 'bg-destructive' : 'bg-foreground'} text-${isOverflowing ? 'destructive' : 'background'}-foreground text-xs font-medium rounded-full`}>
              {isOverflowing ? 'Outside Safe Area!' : 'Black Line = Cut Size | Outer = Bleed Area'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

BadgeCanvas.displayName = 'BadgeCanvas';

export default BadgeCanvas;
